/** Restore field purpose, date context and contrast in the inquiry app. */
const hostSelector = 'shopify-forms-embed';
const bindings = new Map();
let documentObserver;
let active = false;
let nextErrorId = 0;

function enhance(root) {
  // These contact and completion-date fields were confirmed required by app validation.
  const requiredInputs = ['first_name', 'last_name', 'phone_number', 'email']
    .map((id) => root.getElementById(id));
  requiredInputs.push(...root.querySelectorAll('[data-testid="date-birthday"] input'));
  for (const input of requiredInputs) {
    if (input instanceof HTMLInputElement && input.getAttribute('aria-required') !== 'true') {
      input.setAttribute('aria-required', 'true');
    }
  }

  // Associate app-generated errors with their fields without replacing app descriptions.
  const errorLinks = new Map();
  for (const error of root.querySelectorAll('output[role="alert"]')) {
    if (!error.textContent.trim()) continue;
    if (!error.id) error.id = 'inquiry-field-error-' + nextErrorId++;
    const fieldset = error.closest('fieldset');
    const container = fieldset || error.previousElementSibling;
    if (!container) continue;
    const controls = container.matches('input,select,textarea,button') ? [container] :
      [...container.querySelectorAll('input:not([type="hidden"]),select,textarea,button[aria-expanded]')];
    for (const control of controls) {
      if (!errorLinks.has(control)) errorLinks.set(control, []);
      errorLinks.get(control).push(error.id);
    }
  }
  for (const group of root.querySelectorAll('[data-testid="date-birthday"]')) {
    const error = group.lastElementChild;
    if (!error?.matches('[class*="_withError_"]') || !error.textContent.trim() || error.querySelector('input')) continue;
    if (!error.id) error.id = 'inquiry-field-error-' + nextErrorId++;
    for (const input of group.querySelectorAll('input')) errorLinks.set(input, [error.id]);
  }
  for (const control of root.querySelectorAll('input,select,textarea,button')) {
    const descriptions = (control.getAttribute('aria-describedby') || '').split(/\s+/)
      .filter((id) => id && !id.startsWith('inquiry-field-error-'));
    descriptions.push(...(errorLinks.get(control) || []));
    const next = [...new Set(descriptions)].join(' ');
    if (next !== (control.getAttribute('aria-describedby') || '')) {
      if (next) control.setAttribute('aria-describedby', next);
      else control.removeAttribute('aria-describedby');
    }
  }

  for (const button of root.querySelectorAll('button[aria-controls]')) {
    const popup = root.getElementById(button.getAttribute('aria-controls'));
    if (popup?.getAttribute('role') === 'listbox' && !popup.hasAttribute('aria-labelledby') && !popup.hasAttribute('aria-label')) {
      const labelledby = button.getAttribute('aria-labelledby');
      const label = button.getAttribute('aria-label');
      if (labelledby) popup.setAttribute('aria-labelledby', labelledby);
      else if (label) popup.setAttribute('aria-label', label);
    }
  }
  const purposes = { first_name: 'given-name', last_name: 'family-name', email: 'email' };
  for (const [id, purpose] of Object.entries(purposes)) {
    const input = root.getElementById(id);
    if (input instanceof HTMLInputElement && input.autocomplete !== purpose) {
      input.autocomplete = purpose;
    }
  }

  // Reuse the merchant's date question, preserving the app's own error references.
  for (const [index, group] of [...root.querySelectorAll('[data-testid="date-birthday"]')].entries()) {
    const question = group.firstElementChild;
    const inputs = group.querySelectorAll('input');
    if (!question?.textContent?.trim() || inputs.length !== 3) continue;
    if (!question.id) question.id = 'inquiry-date-purpose-' + index;
    if (group.getAttribute('role') !== 'group') group.setAttribute('role', 'group');
    if (group.getAttribute('aria-labelledby') !== question.id) {
      group.setAttribute('aria-labelledby', question.id);
    }
    for (const input of inputs) {
      const descriptions = new Set((input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
      if (!descriptions.has(question.id)) {
        descriptions.add(question.id);
        input.setAttribute('aria-describedby', [...descriptions].join(' '));
      }
    }
  }
}

function bind(host) {
  if (!active || !host.isConnected || bindings.has(host) || !host.shadowRoot) return;
  const root = host.shadowRoot;
  // Keep merchant colors while removing low-contrast alpha from labels and boundaries.
  host.style.setProperty('--form-placeholder-color', 'var(--form-text-color)');
  host.style.setProperty('--form-field-border', '0 0 0 1px var(--form-text-color)');
  enhance(root);
  let pendingReturn;
  const restoreFocus = () => {
    if (!pendingReturn || pendingReturn.popup.isConnected) return;
    if (!root.activeElement && pendingReturn.button.isConnected) pendingReturn.button.focus();
    pendingReturn = undefined;
  };
  const onKeydown = (event) => {
    if (event.key !== 'Escape' || !(event.target instanceof Element)) return;
    const popup = event.target.closest('[role="listbox"]');
    if (!popup?.id) return;
    const button = [...root.querySelectorAll('button[aria-controls]')]
      .find((control) => control.getAttribute('aria-controls') === popup.id);
    if (!button) return;
    pendingReturn = { popup, button };
    queueMicrotask(restoreFocus);
  };
  root.addEventListener('keydown', onKeydown, true);
  const observer = new MutationObserver(() => { enhance(root); restoreFocus(); });
  observer.observe(root, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['autocomplete', 'aria-describedby', 'aria-controls', 'aria-expanded'],
  });
  bindings.set(host, { observer, root, onKeydown });
}

function scan() {
  for (const [host, binding] of bindings) {
    if (!host.isConnected) {
      binding.observer.disconnect();
      binding.root.removeEventListener('keydown', binding.onKeydown, true);
      bindings.delete(host);
    }
  }
  for (const host of document.querySelectorAll(hostSelector)) bind(host);
}

function start() {
  if (active || !document.body) return;
  active = true;
  scan();
  documentObserver = new MutationObserver(scan);
  documentObserver.observe(document.body, { childList: true, subtree: true });
  customElements.whenDefined(hostSelector).then(() => { if (active) scan(); });
}

function stop() {
  active = false;
  documentObserver?.disconnect();
  for (const binding of bindings.values()) {
    binding.observer.disconnect();
    binding.root.removeEventListener('keydown', binding.onKeydown, true);
  }
  bindings.clear();
}

window.addEventListener('pagehide', stop);
window.addEventListener('pageshow', start);
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
else start();
